<template>
  <div class="w-full" data-testid="account-popup">
    <!-- Title Section -->
    <div class="mb-5 self-stretch relative w-full flex items-center justify-center gap-2">
        <div class="self-stretch flex flex-col text-center items-center gap-1">
            <div class="self-stretch text-center text-white text-2xl font-bold font-['Inter'] leading-[30px]" data-testid="account-title">
            {{ $t('settings_title') !== 'settings_title' ? $t('settings_title') : 'Settings' }}
            </div>
        </div>
    </div>

    <div v-if="!authorized" class="flex justify-center mb-6">
        <a href="javascript:;" @click="signIn" class="text-[#6CBFFF] text-[1.125rem] font-['Inter']" data-testid="account-signin-link">
            {{ $t('signin_title') }} / {{ $t('register_title') }}
        </a>
    </div>

    <div v-else class="rounded-[12px] bg-[#222a33] px-3 pb-2 py-2 mb-6" data-testid="account-info-container">
        <a
            href="javascript:;"
            @click="setProfilePage"
            v-show="user"
            class="flex items-center justify-between font-['Inter'] pb-2 w-full border-b border-[rgba(57,57,57,0.5)]"
            data-testid="profile-section">
            <span v-if="authorized" class="flex flex-col">
                <i class="text-white not-italic text-[1.125rem] font-normal" data-testid="user-name">
                {{ user.name }}
                </i>
                <b class="text-[#8a8e92] text-[0.85rem] font-normal" data-testid="user-email">
                {{ user.email }}
                </b>
            </span>
            <span v-else >
                <i class="text-white not-italic text-[1.125rem] font-normal" data-testid="guest-user-name">
                {{ user.name }}
                </i>
                <b class="text-[#8a8e92] text-[0.75rem] font-normal capitalize" data-testid="guest-user-label">
                {{ $t('common_guest') }}
                </b>
            </span>
            <span v-if="user.language" class="flex items-center" data-testid="user-language">
                <img :src="'/data/flags/' + user.language + '.png'" class="w-[20px] h-[20px] mr-2 rounded-full" data-testid="user-language-flag" />
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </a>
        <a
            href="javascript:;"
            @click="setSubscriptionPage"
            class="flex flex-col gap-[5px] py-2 font-['Inter'] w-full"
            data-testid="subscription-section">
            <span class="flex items-center justify-between gap-2">
                <i class="text-white/90 not-italic font-normal flex-shrink-0">
                Plan
                </i>
                <span v-if="user.language" class="flex items-center min-w-0">
                    <div class="text-[#8a8e92] not-italic font-normal mr-2 truncate max-w-[150px]">{{ user.subscription.title === "Free" ? "Free Trial" : user.subscription.title }}</div>
                    <img src="@/assets/icons/arrow-right.svg" class="w-[20px] flex-shrink-0" />
                </span>
            </span>
            <span class="flex items-center justify-between gap-2">
                <b class="text-white/90 text-[0.85rem] font-normal">
                Time remaining
                </b>
                <div v-if="user.language" class="text-[#8a8e92] text-[0.85rem] font-normal">{{ asrHoursLeft }} {{ asrMinutesLeft }} {{ asrSecondsLeft }}</div>
            </span>
        </a>
        <div class="rounded-[3px] h-[4px] self-stretch bg-[#4d555e] overflow-hidden">
            <div
                class="h-full bg-[#6CBFFF] rounded-[3px]"
                :style="{ width: timeRemainingPercentage + '%' }">
            </div>
        </div>
    </div>

    <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal">Menu</div>
        <router-link
            to="/organization"
            @click="this.selfClose()"
            v-if="authorized && isOrganizationAdmin"
            class="flex items-center justify-between font-['Inter'] py-2 w-full">
          <div class="flex flex-row">
            <img class="w-[16px] mr-2" src="@/assets/icons/licenses.svg"/>
            <span class="text-white/90 not-italic font-normal">
                    {{ $t('manage_org_menu') }}
                  </span>
          </div>

          <span class="flex items-center">
                    <div class="text-[#8a8e92] not-italic font-normal mr-2 text-ellipsis text-nowrap overflow-hidden max-w-[180px]">{{ this.user.organization.name }}</div>
                    <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
                </span>
        </router-link>
        <a
            href="javascript:;"
            @click="setTranscriptsPage"
            v-if="authorized"
            class="flex items-center justify-between font-['Inter'] py-2 w-full">
            <div class="flex flex-row">
              <img class="w-[16px] mr-2" src="@/assets/icons/transcripts-icon-grey.svg"/>
              <span class="text-white/90 not-italic font-normal">
                {{ $t('transcripts_title')}}
              </span>
            </div>

            <span class="flex items-center">
                <div class="text-[#8a8e92] not-italic font-normal mr-2">{{ this.transcripts.length }}</div>
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </a>
        <router-link
            to="/faq"
            @click="selfClose()"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="faq-link">
            <div class="flex flex-row">
              <img class="w-[16px] mr-2" src="@/assets/icons/help-icon.svg"/>
              <span class="text-white/90 not-italic font-normal">
                {{ $t('questions_answers') !== 'questions_answers' ? $t('questions_answers') : 'Questions / Answers' }}
              </span>
            </div>

            <span class="flex items-center">
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </router-link>
        <a
            href="javascript:;"
            @click="openSupportRequest"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="support-request-link">
            <div class="flex flex-row items-center">
              <EnvelopeIcon class="size-4 mr-2 text-white" aria-hidden="true" />
              <span class="text-white/90 not-italic font-normal">
                {{ $t('support_request') !== 'support_request' ? $t('support_request') : 'Support request' }}
              </span>
            </div>

            <span class="flex items-center">
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </a>
        <a
            href="javascript:;"
            @click="shareForum"
            class="flex items-center justify-between font-['Inter'] pt-2 w-full">
            <div class="flex flex-row">
              <img class="w-[16px] mr-2" src="@/assets/icons/heart.svg"/>
              <span class="text-white/90 not-italic font-normal">
                {{ $t('share_forum') !== 'share_forum' ? $t('share_forum') : 'Share Forum' }}
              </span>
            </div>

            <span class="flex items-center">
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </a>
    </div>

    <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal" data-testid="application-section">Application</div>
        <locale-picker inline :initial="$i18n.locale" />

        <a
            :href="termsOfUseUrl"
            target="_blank"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="terms-of-use-link">
            <span class="text-white/90 not-italic font-normal">{{ $t('terms_of_use') }}</span>
            <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
        </a>
        <router-link
            to="/terms"
            @click="selfClose()"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="terms-of-service-link">
            <span class="text-white/90 not-italic font-normal">{{ $t('terms_of_service') }}</span>
            <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
        </router-link>
        <a
            :href="privacyPolicyUrl"
            target="_blank"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="privacy-policy-link">
            <span class="text-white/90 not-italic font-normal">{{ $t('support_privacy_title') }}</span>
            <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
        </a>
        <a
            :href="privacyPortalUrl"
            target="_blank"
            class="flex items-center justify-between font-['Inter'] pt-2 w-full"
            data-testid="privacy-portal-link">
            <span class="text-white/90 not-italic font-normal">{{ $t('privacy_portal_title') }}</span>
            <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
        </a>
    </div>

    <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal" data-testid="diagnostics-section">
            {{ $t('diagnostics_title') !== 'diagnostics_title' ? $t('diagnostics_title') : 'Diagnostics' }}
        </div>
        <router-link
            to="/logs"
            @click="selfClose()"
            class="flex items-center justify-between font-['Inter'] py-2 w-full"
            data-testid="logs-link">
            <div class="flex flex-row items-center">
              <lib-icon name="development/terminal-square/outline" size="16" color="white" class="mr-2" />
              <span class="text-white/90 not-italic font-normal">
                {{ $t('logs_title') !== 'logs_title' ? $t('logs_title') : 'Logs' }}
              </span>
            </div>

            <span class="flex items-center">
                <img src="@/assets/icons/arrow-right.svg" class="w-[20px]" />
            </span>
        </router-link>
        <div class="flex items-center justify-between font-['Inter'] pt-2 w-full" data-testid="version-info-section">
            <span class="text-white/90 not-italic font-normal">
              {{ $t('version') !== 'version' ? $t('version') : 'Version' }}
            </span>
            <div class="text-[#8a8e92] not-italic font-normal" data-testid="version-value">{{ appVersion }}</div>
        </div>
    </div>
    <div v-if="authorized" class="flex justify-center mt-4 mb-2">
  <a href="javascript:;" class="text-[#EA4335] no-underline flex items-center whitespace-nowrap" @click="logout">
    <img src="@/assets/icons/logout-icon.svg" alt="Sign out" class="max-w-[20px] mr-[10px]">
    {{ $t('settings_signout')}}
  </a>
</div>

        <sheet-modal v-model="showAccountTypeModal" exact-width="w-[420px]" :blocking="true">
          <account-type-selection-modal
            @selected="onAccountTypeSelected"
            @close="showAccountTypeModal = false"
          />
        </sheet-modal>
  </div>
</template>

<script>
// @ is an alias to /src
import TranscriptService from "@/services/transcript.service";
import GlossaryService from '@/services/glossary.service';

import { mapMutations, mapState } from 'vuex';
import emitter from "@/services/emitter.service";
import SheetModal from '@/components/ui/SheetModal.vue';
import AccountTypeSelectionModal from '@/components/auth/AccountTypeSelectionModal.vue';

import permissions from "@/services/permissions.service";
import { Constants } from '@/cfg/constants';
import pkg from '../../../package.json';
import { EnvelopeIcon } from '@heroicons/vue/24/outline';
import LocalePicker from '@/components/LocalePicker.vue';
import LibIcon from '@/components/ui/LibIcon.vue';

export default {
  name: 'AccountPopup',
  components: {
    SheetModal,
    AccountTypeSelectionModal,
    EnvelopeIcon,
    LocalePicker,
    LibIcon,
  },
  data() {
    return {
      errors: {},
      transcripts: [],
      terms: [],
      shareSupported: false,
      showAccountTypeModal: false,
      appVersion: pkg.version,
    }
  },
  methods: {
    ...mapMutations('nonpersist', ['setUser']),
    logout() {
      this.selfClose();
      this.$forumAuth.logout();
    },
    signIn() {
      this.selfClose();
      emitter.emit('open-terms-agreement-modal');
    },
    setProfilePage() {
      this.selfClose();
      emitter.emit('open-profile-popup');
    },
    setSubscriptionPage() {
      this.selfClose();
      emitter.emit('open-subscription-popup');
    },
    setTranscriptsPage() {
      this.selfClose();
      this.$router.push({ path: '/transcripts' });
    },
    openBeacon() {
      window.Beacon('open');
    },
    openSupportRequest() {
      this.selfClose();
      this.openBeacon();
    },
    selfClose() {
      this.$emitter.emit('modalClose');
    },
    clipboardCopy() {
      const defaultMessage =
      `Check out Sorenson Forum!\nIt's an app that translates and captions speech simultaneously in dozens of the world's most common languages that audiences can access on their own devices.\n\n${Constants.REGION_CONFIG.forumUrl}`;
      navigator.clipboard.writeText(this.$t('share_forum_text') !== 'share_forum_text' ? this.$t('share_forum_text') : defaultMessage);
      const message = 'Information about Forum was copied to your clipboard';
      this.$toast.success(message); //remove this hard coded english once we have translation for this message.
    },
    async shareForum() {
      if (this.shareSupported) {
        try {
          const shareData = {
            title: this.$t('share_forum_title') !== 'share_forum_title' ? this.$t('share_forum_title') : "Check out Sorenson Forum!",
            text: this.$t('share_forum_text') !== 'share_forum_text' ? this.$t('share_forum_text') : `Check out Sorenson Forum!\nIt's an app that translates and captions speech simultaneously in dozens of the world's most common languages that audiences can access on their own devices.\n\n${Constants.REGION_CONFIG.forumUrl}`,
          };

          await navigator.share(shareData);
        } catch (error) {
          console.error('Error sharing:', error);
          // Fallback to clipboard copy if sharing fails
          this.clipboardCopy();
        }
      } else {
        // Fallback for browsers that don't support sharing
        this.clipboardCopy();
      }
    },
  },
  computed: {
    ...mapState('auth', ['token', 'authorized', 'accountType']),
    ...mapState('nonpersist', ['user']),
    ...mapState('locale', ['plansMapping']),

    termsOfUseUrl() {
      return Constants.REGION_CONFIG.termsUrl;
    },
    privacyPolicyUrl() {
      return Constants.REGION_CONFIG.privacyUrl;
    },
    privacyPortalUrl() {
      return Constants.REGION_CONFIG.privacyPortalUrl;
    },

    normalizedAccountType() {
      return this.accountType || 'personal';
    },

    timeRemainingPercentage() {
        const totalAllowedSeconds = this.user.features.asr.total_allowed_items;
        const remainingSeconds = this.user.features.asr.left_allowed_items;

        return Math.max(0, Math.min(100, (remainingSeconds / totalAllowedSeconds) * 100));
    },

    asrHoursLeft() {
      return Math.floor(this.user.features.asr.left_allowed_items / 60 / 60) + this.$t('common_hours_short');
    },
    asrMinutesLeft() {
      return Math.floor(this.user.features.asr.left_allowed_items / 60 % 60) + this.$t('common_minutes_short');
    },
    asrSecondsLeft() {
      return this.user.features.asr.left_allowed_items % 60 + this.$t('common_seconds_short')
    },
    subscription() {
      return this.user.subscription ?? false;
    },
    canGlossary() {
      return permissions.can('custom_terms', this.user);
    },
    isOrganizationAdmin() {
      return this.user.organization && this.user.organization.id && this.user.organization.role === 'admin';
    }
  },
  async mounted() {
    if (this.token) {
      await this.$store.dispatch('auth/userMe', {
        token: this.token,
        room: false,
      });
    }

    if (this.token) {
      //this seems kind of slow just to get the number of transcripts, maybe find a better way to do this
      await TranscriptService.getTranscriptList(this.token).then((data) => {
        this.transcripts = data.data.result.transcripts;
      });

      const response = await GlossaryService.getTerms();
      this.terms = response.data.result;
    }


    // Check if Web Share API is supported
    this.shareSupported = navigator && navigator.share;
  }
}
</script>
